import { Minus, Plus } from 'lucide-react'
import type React from 'react'

import { cn } from '@modules/lib/lib'
import { Button } from '@modules/shadcn/ui/Button/Button'

import { ZoomResetButton } from './ZoomResetButton'

/** 倍率の上限・下限に達したかを判定する誤差。ホイールの刻み（0.1）より十分小さく取る */
const SCALE_EPSILON = 0.001

interface ZoomControlsProps {
  /** 現在のズーム倍率（ZoomableImage の onScaleChange で受け取った値） */
  scale: number
  /** 押下時の処理。通常は `transformRef.current?.resetTransform()` */
  onReset: () => void
  /** 拡大ボタンの押下時の処理。通常は `transformRef.current?.zoomIn(AI_COLOR_ZOOM_STEP)` */
  onZoomIn: () => void
  /**
   * 縮小ボタンの押下時の処理。通常は `transformRef.current?.zoomOut(AI_COLOR_ZOOM_STEP)`。
   * 未指定なら縮小ボタンを描画しない（プレビュー表示では縮小操作を提供しない。issue #2417）。
   */
  onZoomOut?: () => void
  /** 最小ズーム倍率。これに達している間は縮小ボタンを押せない */
  minScale: number
  /** 最大ズーム倍率。これに達している間は拡大ボタンを押せない */
  maxScale: number
  className?: string
}

/**
 * AIカラーのキャンバスのズーム操作ボタン群（`-` / 原寸 / `+`。issue #2417）。
 *
 * ホイール・ピンチに頼らず倍率を変えられるようにするためのもの。
 * `ZoomResetButton`（元のサイズに戻す）を内包し、等倍のときは原寸ボタンだけが消える
 * （`+` / `-` は常に出ていないと「拡大できること」自体が伝わらないため）。
 *
 * 縮小ボタンだけ `onZoomOut` を省いて消せる。プレビュー表示では縮小操作を提供しないため
 * （issue #2417）。積算の図面キャンバスはこのコンポーネントを使わず、内包している
 * `ZoomResetButton` を単体で使い続ける。
 *
 * 配置は呼び出し側で決める（`ZoomableImage` の中に入れない）。AIカラーは縮小時に外枠の高さを
 * 負マージンで詰めて overflow-hidden で切るため、`ZoomableImage` 内に絶対配置すると
 * 縮小したときに見切れてしまう。
 */
export const ZoomControls: React.FC<ZoomControlsProps> = ({
  scale,
  onReset,
  onZoomIn,
  onZoomOut,
  minScale,
  maxScale,
  className,
}) => (
  // flex-wrap: 画像パネルが狭いとき（モバイルの 2 カラム表示では 1 枚あたり 170px 前後）に
  // 3 ボタンが横並びで収まらず、スロット側の overflow-hidden で左端が切れる。
  // 収まらない分は折り返して右寄せで積む。
  <div className={cn('flex flex-wrap items-center justify-end gap-1', className)}>
    {onZoomOut && (
      <Button
        aria-label="縮小"
        className="h-7 w-7 bg-background/95 p-0 shadow-sm"
        disabled={scale <= minScale + SCALE_EPSILON}
        onClick={onZoomOut}
        size="sm"
        type="button"
        variant="outline"
      >
        <Minus aria-hidden className="size-3.5" />
      </Button>
    )}
    <ZoomResetButton onReset={onReset} scale={scale} />
    <Button
      aria-label="拡大"
      className="h-7 w-7 bg-background/95 p-0 shadow-sm"
      disabled={scale >= maxScale - SCALE_EPSILON}
      onClick={onZoomIn}
      size="sm"
      type="button"
      variant="outline"
    >
      <Plus aria-hidden className="size-3.5" />
    </Button>
  </div>
)
